import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Box, Card, CardContent, CardHeader, Typography, Button, Alert, CircularProgress, Chip, Grid, LinearProgress } from '@mui/material'
import { ArrowBack } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { analysisService, type SessionResults } from '@/services/analysisService'
import { useAuth } from '@/contexts/AuthContext'

export default function LiveAnalysisSessionDetailPage() {
  const { t } = useTranslation('analysis')
  const { t: tCommon } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { token } = useAuth()
  const [results, setResults] = useState<SessionResults | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (token && id) {
      loadResults()
    }
  }, [token, id])

  const loadResults = async () => {
    if (!id) return

    try {
      setLoading(true)
      setError(null)
      const data = await analysisService.getLiveAnalysisResults(parseInt(id))
      setResults(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('failedToLoadResults'))
    } finally {
      setLoading(false)
    }
  }

  const getEmotionColor = (emotion: string) => {
    const colors: Record<string, string> = {
      happy: '#22c55e',
      sad: '#3b82f6',
      angry: '#ef4444',
      fearful: '#8b5cf6',
      surprised: '#f59e0b',
      disgusted: '#a855f7',
      neutral: '#6b7280',
    }
    return colors[emotion] || '#6b7280'
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString()
  }

  const formatTimestamp = (timestampMs: number) => {
    return new Date(timestampMs).toLocaleTimeString()
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button variant="contained" onClick={loadResults}>{t('retry')}</Button>
      </Box>
    )
  }

  if (!results) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">{t('noResultsFound')}</Alert>
      </Box>
    )
  }

  const { session, frames, summary } = results

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/live-analysis')}>
          {t('backToSessions')}
        </Button>
      </Box>

      <Grid container spacing={3}>
        <Grid size={12}>
          <Card>
            <CardHeader title={t('sessionInformation')} />
            <CardContent>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">{t('sessionId')}</Typography>
                  <Typography variant="body1">{session.id}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">{tCommon('status')}</Typography>
                  <Chip
                    label={session.status}
                    color={session.status === 'completed' ? 'primary' : session.status === 'active' ? 'success' : 'error'}
                    size="small"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">{t('startedAt')}</Typography>
                  <Typography variant="body1">{formatDate(session.started_at)}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">{t('endedAt')}</Typography>
                  <Typography variant="body1">{session.ended_at ? formatDate(session.ended_at) : t('notAvailable')}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">{t('totalFrames')}</Typography>
                  <Typography variant="body1">{session.total_frames}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={12}>
          <Card>
            <CardHeader title={t('summary')} />
            <CardContent>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">{t('totalFramesAnalyzed')}</Typography>
                  <Typography variant="h6">{summary.total_frames}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">{t('framesWithFaceDetected')}</Typography>
                  <Typography variant="h6">{summary.frames_with_face}</Typography>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography variant="body2" color="text.secondary">{t('averageConfidence')}</Typography>
                  <Typography variant="h6">{(summary.average_confidence * 100).toFixed(1)}%</Typography>
                </Grid>
                <Grid size={12}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('dominantEmotionsDistribution')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {Object.entries(summary.dominant_emotions).map(([emotion, count]) => (
                      <Box key={emotion}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{emotion}</Typography>
                          <Typography variant="body2">{t('framesCount', { count })}</Typography>
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={(count / summary.frames_with_face) * 100}
                          sx={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: '#e5e7eb',
                            '& .MuiLinearProgress-bar': {
                              backgroundColor: getEmotionColor(emotion),
                            },
                          }}
                        />
                      </Box>
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={12}>
          <Card>
            <CardHeader title={t('analysisFrames', { count: frames.length })} />
            <CardContent>
              {frames.length === 0 ? (
                <Alert severity="info">{t('noFramesAnalyzed')}</Alert>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 600, overflowY: 'auto' }}>
                  {frames.map((frame) => (
                    <Card key={frame.id} variant="outlined">
                      <CardContent>
                        <Grid container spacing={2}>
                          <Grid size={{ xs: 12, sm: 3 }}>
                            <Typography variant="body2" color="text.secondary">
                              {t('frameNumber', { index: frame.frame_index })}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatTimestamp(frame.timestamp_ms)}
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 12, sm: 9 }}>
                            {frame.face_detected ? (
                              <Box>
                                <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                                  <Chip
                                    label={frame.dominant_emotion?.toUpperCase() || t('notAvailable')}
                                    sx={{
                                      backgroundColor: frame.dominant_emotion ? getEmotionColor(frame.dominant_emotion) : '#6b7280',
                                      color: 'white',
                                      fontWeight: 'bold',
                                    }}
                                    size="small"
                                  />
                                  <Typography variant="body2">
                                    {t('confidencePercent', {
                                      value: (frame.dominant_confidence ? frame.dominant_confidence * 100 : 0).toFixed(1),
                                    })}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                  {frame.emotions.map((emotion, idx) => (
                                    <Chip
                                      key={idx}
                                      label={`${emotion.emotion}: ${(emotion.confidence * 100).toFixed(1)}%`}
                                      size="small"
                                      variant="outlined"
                                    />
                                  ))}
                                </Box>
                              </Box>
                            ) : (
                              <Alert severity="warning" sx={{ py: 0 }}>{t('noFaceDetectedFrame')}</Alert>
                            )}
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}
